import { useEffect, useState } from "react";
import { CalculatorPage } from "../../components/CalculatorPage";
import { ResultExplainer } from "../../components/ResultExplainer";
import { FilingStatusSelector } from "../../components/FilingStatusSelector";
import { ComparisonCard } from "../../components/ComparisonCard";
import { taxAPI, formatCurrency } from "../../lib/api";
import { buildCalculatorSchema } from "../../hooks/useSEO";
import { ComparisonChart } from "../../components/ComparisonChart";
import { EmailGatePdfButton } from "../../components/EmailGatePdfButton";

const StandardDeductionCalculator = () => {
  const [filing, setFiling] = useState("single");
  const [result, setResult] = useState(null);

  useEffect(() => {
    taxAPI.standardDeduction(filing).then(setResult).catch(() => {});
  }, [filing]);

  const inputPanel = (
    <div className="space-y-5">
      <div>
        <label className="font-body text-sm font-semibold block mb-2">Filing Status</label>
        <FilingStatusSelector value={filing} onChange={setFiling} testid="std-filing" />
      </div>
      <div className="mt-6 p-5 bg-muted border border-foreground/15">
        <span className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground mb-2 block">Note</span>
        <p className="font-body text-sm">
          OBBBA kept the higher standard deduction permanent and permanently eliminated personal
          exemptions. For 2026, the standard deduction increased with inflation — plus a small
          OBBBA bonus bump on the lower brackets.
        </p>
      </div>
    </div>
  );

  const resultPanel = result ? (
    <div className="space-y-6" data-testid="std-result">
      <ComparisonCard
        testid="std-comparison"
        rows={[
          {
            label: "Standard Deduction",
            value2025: result.year_2025,
            value2026: result.year_2026,
            format: "currency",
            invertImpact: true,
          },
          {
            label: "Personal Exemption",
            value2025: 0,
            value2026: result.personal_exemption_2026,
            format: "currency",
            invertImpact: true,
            hint: "Per filer, spouse, and dependent in 2026",
          },
        ]}
      />
      <div className="border-2 border-foreground bg-card p-6">
        <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-muted-foreground block mb-2">Observation</span>
        <p className="font-heading text-xl leading-snug">
          Your standard deduction shrinks by{" "}
          <span className="text-destructive font-semibold">
            {formatCurrency(result.reduction)}
          </span>{" "}
          — a {result.percent_reduction}% cut — in 2026.
        </p>
      </div>

      <ComparisonChart
        testid="std-chart"
        title="Deduction capacity: 2025 vs 2026"
        rows={[
          { label: "Std. Deduction", v2025: result.year_2025, v2026: result.year_2026 },
          { label: "Personal Exempt.", v2025: 0, v2026: result.personal_exemption_2026 },
          { label: "Combined", v2025: result.year_2025, v2026: result.year_2026 + result.personal_exemption_2026 },
        ]}
      />

      <EmailGatePdfButton
        testid="std-pdf"
        filename="taxshift-2026-standard-deduction.pdf"
        pdfPayload={{
          title: "2026 Standard Deduction Analysis",
          subtitle: `Filing status: ${result.filing_status.replace("_", " ").toUpperCase()}`,
          inputs: [{ label: "Filing Status", value: result.filing_status.replace("_", " ").toUpperCase() }],
          headline: `Your standard deduction shrinks by ${formatCurrency(result.reduction)} — a ${result.percent_reduction}% reduction.`,
          rows: [
            { label: "Standard Deduction", v2025: formatCurrency(result.year_2025), v2026: formatCurrency(result.year_2026) },
            { label: "Personal Exemption", v2025: formatCurrency(0), v2026: formatCurrency(result.personal_exemption_2026) },
          ],
        }}
      />
      <ResultExplainer slug="standard-deduction-2026" />
    </div>
  ) : null;

  return (
    <CalculatorPage
      title="2026 Standard Deduction Calculator"
      slug="standard-deduction-2026"
      overline="Calculator · Deductions"
      description="See the exact 2025 and 2026 standard deduction amounts under OBBBA. Personal exemptions remain permanently eliminated. This calculator shows year-over-year inflation adjustments across all filing statuses."
      seoTitle="2026 Standard Deduction Calculator — OBBBA Amounts"
      seoKeywords="standard deduction 2026, obbba standard deduction, 16100 standard deduction, 32200 married deduction"
      jsonLd={buildCalculatorSchema({
        name: "2026 Standard Deduction Calculator",
        description: "Free calculator comparing the 2025 vs 2026 standard deduction across filing statuses.",
        url: "/calculator/standard-deduction-2026",
      })}
      inputPanel={inputPanel}
      resultPanel={resultPanel}
      education={
        <>
          <h2 className="font-heading text-3xl md:text-4xl font-bold tracking-tight mb-6">
            OBBBA kept the higher standard deduction
          </h2>
          <div className="font-body text-base leading-relaxed space-y-4 text-foreground/85">
            <p>
              The TCJA's near-doubled standard deduction is now permanent under OBBBA. For 2026,
              the standard deduction increased with inflation — plus a small OBBBA bonus bump —
              to <span className="font-semibold">$16,100 (single)</span> and{" "}
              <span className="font-semibold">$32,200 (married joint)</span>.
            </p>
            <p>
              Personal exemptions remain permanently at zero. OBBBA eliminated the scheduled 2026
              reversion to pre-2018 rules, so there's no $5,300 personal exemption returning.
            </p>
            <p>
              With the SALT cap raised from $10,000 to $40,000, many more taxpayers will find
              itemizing worthwhile — but for most households, the standard deduction still wins.
            </p>
          </div>
        </>
      }
      relatedSlugs={[
        { slug: "tax-bracket-2026", label: "Tax Bracket" },
        { slug: "child-tax-credit-2026", label: "Child Tax Credit" },
        { slug: "salt-deduction-2026", label: "SALT Deduction" },
      ]}
    />
  );
};

export default StandardDeductionCalculator;
